import clsx from 'clsx';
import { Activity } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link, useMatch } from 'react-router';
import { useMe } from '@/api/hooks/auth';
import { useRunningJobs } from '@/api/hooks/jobs';
import { Popover, ProgressBar } from '@/components';
import { formatPercent, humanize } from '@/lib/format';
import { paths } from '@/routing';
import styles from './TopBar.module.css';

/** Running-jobs badge (polls `/api/jobs?status=running`) with a dropdown list. */
export function JobsIndicator() {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const running = useRunningJobs();
  const me = useMe();
  const match = useMatch('/projects/:pid/*');
  const pid = match?.params.pid ? Number(match.params.pid) : null;
  const count = running.data?.total ?? 0;
  const allJobsPath = pid ? paths.projectJobs(pid) : me.data?.is_admin ? paths.adminJobs() : null;

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={clsx(styles.iconTrigger, count > 0 && styles.busy)}
        aria-label={count > 0 ? `${count} running job${count === 1 ? '' : 's'}` : 'Jobs'}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Activity aria-hidden />
        {count > 0 && <span className={styles.count}>{count}</span>}
      </button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        placement="bottom-end"
        aria-label="Running jobs"
        className={styles.jobsPanel}
        autoFocus={false}
      >
        <div className={styles.jobsHeader}>
          <h2>Running jobs</h2>
          {allJobsPath && (
            <Link to={allJobsPath} onClick={() => setOpen(false)}>
              All jobs
            </Link>
          )}
        </div>
        {count === 0 ? (
          <p className={styles.jobsEmpty}>Nothing is running right now.</p>
        ) : (
          <ul className={styles.jobsList}>
            {running.data?.items.map((job) => (
              <li key={job.id} className={styles.jobItem}>
                <div className={styles.jobLine}>
                  <span className="truncate">
                    {humanize(job.kind)} <span className="subtle">#{job.id}</span>
                  </span>
                  <span className="num subtle">{formatPercent(job.progress)}</span>
                </div>
                <ProgressBar value={job.progress} label={`${humanize(job.kind)} #${job.id}`} size="sm" />
                <div className={styles.jobLine}>
                  <span className="truncate subtle small">{job.message ?? 'Running…'}</span>
                  {job.project_id !== null && (
                    <Link
                      className="small"
                      to={paths.projectJobs(job.project_id)}
                      onClick={() => setOpen(false)}
                    >
                      Project {job.project_id}
                    </Link>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Popover>
    </>
  );
}
